import {
  EditEventForm,
  editEventFormDefault,
  Event,
  Actions,
  QueryResult,
  Subjects,
} from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';
import {
  ConfirmModal,
  dateColumn,
  EMPTY_VALUE,
  Section,
  tableStyleOverrides,
  textColumn,
  useNotification,
} from '@dispatch/web/ui';
import { Stack } from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useDeleteEvent } from '@dispatch/shared/hooks';
import { Ability } from '@casl/ability';
import { getTimeWithOffset } from '@dispatch/shared';
import { format } from 'date-fns';

const DAY_IN_MS = 86340000; // 23 hours, 59 minutes, and 0 seconds

export type ResourceEventsProps = {
  eventsQuery?: QueryResult<Event[] | undefined, unknown>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  setEditEventForm: (editEventForm: EditEventForm) => void;
  setOpenEditEvent: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  isReadOnly: boolean;
};
export default function ResourceEvents({
  setEditEventForm,
  setOpenEditEvent,
  setIsEdit,
  eventsQuery,
  permissions,
  isReadOnly = false,
}: ResourceEventsProps) {
  const onClickEditEvent = (editEventForm: EditEventForm) => {
    setIsEdit(true);
    setEditEventForm(editEventForm);
    setOpenEditEvent(true);
  };

  const createEventForm = (
    startDateTime: Date,
    endDateTime: Date,
    name: string,
    eventId: string,
    timeZoneId: string,
    category?: string,
    postalCode?: string,
    isForced?: boolean,
    createdBy?: string,
    editedBy?: string
  ): EditEventForm => {
    return {
      ...editEventFormDefault,
      startDateTime: getTimeWithOffset(timeZoneId, startDateTime),
      endDateTime: getTimeWithOffset(timeZoneId, endDateTime),
      name,
      eventId,
      timeZoneId,
      category,
      postalCode,
      isForced,
      createdBy,
      editedBy,
    };
  };

  const hasUpsertPermissions =
    permissions?.can?.('upsert', 'resource-events') || false;

  const padTo2Digits = (number: number) => {
    return number.toString().padStart(2, '0');
  };

  const convertMsToDHM = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    let hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    minutes = minutes % 60;
    hours = hours % 24;

    return `${padTo2Digits(days)}:${padTo2Digits(hours)}:${padTo2Digits(
      minutes
    )}`;
  };

  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const { notifyError, notifySuccess } = useNotification();
  const [toDelete, setToDelete] = useState<{
    resourceId: string;
    eventId: string;
  }>({
    resourceId: '',
    eventId: '',
  });

  const onDeleteEvent = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess('Success', 'Event was deleted');
      eventsQuery?.refetch && eventsQuery?.refetch();
    } else {
      notifyError('Error', 'Error deleting event');
    }

    setOpenConfirmDeleteModal(false);
  };
  const { deleteEvent } = useDeleteEvent(onDeleteEvent);

  const onDeleteClick = (resourceId: string, eventId: string) => {
    if (resourceId && eventId) {
      setOpenConfirmDeleteModal(true);
      setToDelete({
        resourceId,
        eventId,
      });
    } else {
      notifyError('Error', 'Error deleting event');
    }
  };

  const renderConfirmDelete = () => {
    return (
      <ConfirmModal
        onConfirm={() => deleteEvent(toDelete)}
        onDeny={() => setOpenConfirmDeleteModal(false)}
        confirmTitle={'Delete Event?'}
        confirmContent={
          'Deleting the selected event will remove blocked time for this Resource and make them available during this time'
        }
        modalType={'delete'}
      />
    );
  };
  const columns = [
    textColumn(
      {
        field: 'eventName',
        headerName: 'Type',
      },
      'resource-event-row'
    ),
    textColumn({
      field: 'startDateTime',
      headerName: 'Start Date Time',
      valueGetter: (params) => {
        const startDateTime = params.row?.startDateTime;
        const endDateTime = params.row?.endDateTime;

        const value =
          getTimeWithOffset(
            params?.row?.timeZoneId,
            new Date(params?.row?.startDateTime)
          ) || EMPTY_VALUE;

        if (
          new Date(endDateTime).getTime() - new Date(startDateTime).getTime() >=
          DAY_IN_MS
        ) {
          return format(new Date(value), 'EEE MM/dd/yyyy');
        }
        return format(new Date(value), 'EEE MM/dd/yyyy hh:mm a');
      },
      sortComparator: (v1, v2, param1, param2) => {
        const date1 = new Date(param1.api.getRow(param1.id)?.startDateTime);
        const date2 = new Date(param2.api.getRow(param2.id)?.startDateTime);
        return date1.getTime() - date2.getTime();
      },
    }),
    textColumn({
      field: 'endDateTime',
      headerName: 'End Date Time',
      valueGetter: (params) => {
        const startDateTime = params.row?.startDateTime;
        const endDateTime = params.row?.endDateTime;

        const value =
          getTimeWithOffset(
            params?.row?.timeZoneId,
            new Date(params?.row?.endDateTime)
          ) || EMPTY_VALUE;

        if (
          new Date(endDateTime).getTime() - new Date(startDateTime).getTime() >=
          DAY_IN_MS
        ) {
          return format(new Date(value), 'EEE MM/dd/yyyy');
        }
        return format(new Date(value), 'EEE MM/dd/yyyy hh:mm a');
      },
      sortComparator: (v1, v2, param1, param2) => {
        const date1 = new Date(param1.api.getRow(param1.id)?.endDateTime);
        const date2 = new Date(param2.api.getRow(param2.id)?.endDateTime);
        return date1.getTime() - date2.getTime();
      },
    }),
    textColumn({
      field: 'duration',
      headerName: 'Duration (dd:hh:mm)',
      valueGetter: (params) => {
        const startDateTime = params.row?.startDateTime;
        const endDateTime = params.row?.endDateTime;

        if (
          new Date(endDateTime).getTime() - new Date(startDateTime).getTime() >=
          DAY_IN_MS
        ) {
          return 'All Day';
        }

        if (startDateTime && endDateTime) {
          return convertMsToDHM(
            new Date(endDateTime).getTime() - new Date(startDateTime).getTime()
          );
        }

        return EMPTY_VALUE;
      },
    }),
    {
      field: 'actions',
      type: 'actions',
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="DELETE"
          disabled={isReadOnly}
          onClick={() =>
            onDeleteClick(params.row?.resourceId, params.row?.externalId)
          }
        />,
      ],
    },
  ];

  return (
    <Section>
      <div style={{ height: '500px' }} data-testid="resource-events-list">
        <DataGridPro
          disableVirtualization={isRunningInJest()}
          loading={eventsQuery?.status === 'loading'}
          rows={eventsQuery?.data || []}
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Events
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Events Found
              </Stack>
            ),
          }}
          columns={columns}
          sx={tableStyleOverrides}
          getRowId={(row) => row.externalId}
          hideFooter={isRunningInJest()}
          hideFooterSelectedRowCount={true}
          disableSelectionOnClick={true}
          onRowClick={(value) => {
            const postalCode =
              value.row?.locationData?.geoJson?.properties?.postalCode || '';

            hasUpsertPermissions &&
              !isReadOnly &&
              onClickEditEvent(
                createEventForm(
                  value.row.startDateTime,
                  value.row.endDateTime,
                  value.row.eventName,
                  value.row.externalId,
                  value.row.timeZoneId,
                  value.row.category,
                  postalCode,
                  value.row.isForced,
                  value.row.createdBy,
                  value.row.editedBy
                )
              );
          }}
          columnVisibilityModel={{
            actions: hasUpsertPermissions,
          }}
          pagination
          autoPageSize
        />
      </div>
      {openConfirmDeleteModal && renderConfirmDelete()}
    </Section>
  );
}
